import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../common/ui/card';
import { Button } from '../../common/ui/button';
import { Text } from '../../common/ui/typography';
import { agentService } from '../../services/agentService';
import { extractApiData } from '../../utils/apiResponseHandler';
import toast from 'react-hot-toast';

const DEFAULT_THEME = {
  primaryColor: '#4f46e5',
  position: 'bottom-right',
  buttonLabel: 'Chat with us',
};

function CopyBlock({ label, value, hint }) {
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Text size="sm" className="font-medium">
          {label}
        </Text>
        <Button type="button" variant="outline" size="sm" onClick={copy} disabled={!value}>
          Copy
        </Button>
      </div>
      {hint ? (
        <Text size="xs" color="muted">
          {hint}
        </Text>
      ) : null}
      <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
        {value || '—'}
      </pre>
    </div>
  );
}

export default function AgentEmbedPanel({ agentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    publicAccess: false,
    allowedOrigins: '',
    theme: { ...DEFAULT_THEME },
  });

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const res = await agentService.getEmbed(agentId);
      const payload = extractApiData(res);
      setData(payload);
      const e = payload?.embed || {};
      setForm({
        enabled: !!e.enabled,
        publicAccess: !!e.publicAccess || !!payload?.isPublic,
        allowedOrigins: (e.allowedOrigins || []).join('\n'),
        theme: {
          ...DEFAULT_THEME,
          ...(e.theme || {}),
        },
      });
    } catch (err) {
      toast.error(err.message || 'Could not load embed settings');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const origins = form.allowedOrigins
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await agentService.saveEmbed(agentId, {
        enabled: form.enabled,
        publicAccess: form.publicAccess,
        channels: ['chat'],
        allowedOrigins: origins,
        theme: {
          primaryColor: form.theme.primaryColor || DEFAULT_THEME.primaryColor,
          position: form.theme.position || DEFAULT_THEME.position,
          buttonLabel: (form.theme.buttonLabel || DEFAULT_THEME.buttonLabel).trim(),
        },
      });
      const payload = extractApiData(res);
      if (payload) setData(payload);
      toast.success('Embed settings saved');
      await load();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!agentId) {
    return <Text color="muted">Save the agent first to configure embed.</Text>;
  }

  if (loading) {
    return <Text color="muted">Loading embed settings…</Text>;
  }

  const installReady = form.enabled && (form.publicAccess || data?.isPublic);

  return (
    <div className="space-y-4 max-w-2xl">
      <Text size="sm" color="muted">
        Add a text chat widget to your website. Visitors talk to this agent in a floating button
        or an embedded panel. Voice calling stays on phone numbers — not in this widget.
      </Text>

      <form onSubmit={handleSave} className="space-y-4">
        <Card className="card-user">
          <CardHeader>
            <CardTitle className="card-user-title text-base">1. Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.enabled}
                onChange={(ev) => setForm((f) => ({ ...f, enabled: ev.target.checked }))}
              />
              <span>
                <span className="font-medium">Enable website embed</span>
                <span className="block text-muted-foreground text-xs mt-0.5">
                  Turns on the public chat page and widget for this agent.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.publicAccess}
                onChange={(ev) => setForm((f) => ({ ...f, publicAccess: ev.target.checked }))}
              />
              <span>
                <span className="font-medium">Allow public visitors</span>
                <span className="block text-muted-foreground text-xs mt-0.5">
                  Required unless the agent is already marked public. Without this, the widget
                  returns “not available”.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>

        <Card className="card-user">
          <CardHeader>
            <CardTitle className="card-user-title text-base">2. Appearance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Button label</label>
              <input
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                maxLength={40}
                value={form.theme.buttonLabel}
                onChange={(ev) =>
                  setForm((f) => ({
                    ...f,
                    theme: { ...f.theme, buttonLabel: ev.target.value },
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Accent color</label>
                <input
                  type="color"
                  value={form.theme.primaryColor}
                  onChange={(ev) =>
                    setForm((f) => ({
                      ...f,
                      theme: { ...f.theme, primaryColor: ev.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Widget position</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.theme.position}
                  onChange={(ev) =>
                    setForm((f) => ({
                      ...f,
                      theme: { ...f.theme, position: ev.target.value },
                    }))
                  }
                >
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-user">
          <CardHeader>
            <CardTitle className="card-user-title text-base">3. Allowed websites</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Text size="sm" color="muted">
              Leave empty to allow any site. Otherwise list origins that may host the widget
              (one per line), e.g. <code className="text-xs">https://yourstore.com</code> or{' '}
              <code className="text-xs">*.yourstore.com</code>.
            </Text>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[88px] font-mono"
              placeholder={'https://yourstore.com\nhttps://www.yourstore.com'}
              value={form.allowedOrigins}
              onChange={(ev) => setForm((f) => ({ ...f, allowedOrigins: ev.target.value }))}
            />
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          {data?.iframeUrl ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(data.iframeUrl, '_blank', 'noopener,noreferrer')}
            >
              Open preview
            </Button>
          ) : null}
          {onClose ? (
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          ) : null}
        </div>
      </form>

      <Card className="card-user">
        <CardHeader>
          <CardTitle className="card-user-title text-base">4. Install on your site</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {!installReady ? (
            <Text size="sm" color="muted">
              Enable embed and allow public visitors, then save — install snippets appear here.
            </Text>
          ) : (
            <>
              <CopyBlock
                label="Floating widget (recommended)"
                hint="Paste before </body> on your site. Shows a chat button that opens this agent."
                value={data?.scriptSnippet}
              />
              <CopyBlock
                label="Inline iframe"
                hint="Paste where you want a fixed chat panel (help page, landing section)."
                value={data?.iframeSnippet}
              />
              <CopyBlock
                label="Direct link"
                hint="Shareable chat URL (same page the widget loads)."
                value={data?.iframeUrl}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
